import React, { useEffect, useMemo, useState } from "react";
// import { FaEye, FaUserCog } from "react-icons/fa";
import styles from "../master/academicConfiguration/AcademicCreate.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../components";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import tiemStyles from "./CreatetimetbListView.module.css";
import PopupModal from "../../../components/PopupModal/PopupModal";

const ViewTimeTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { CLASSID, SECTION, SHIFTID, DAY } = location.state || {};

  const classId = typeof CLASSID === "object" ? CLASSID.value : CLASSID;
  const sectionId = typeof SECTION === "object" ? SECTION.value : SECTION;
  const shiftId = typeof SHIFTID === "object" ? SHIFTID.value : SHIFTID;

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const end_date = clientData.end_date;
  const start_date = clientData.start_date;

  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels;

  // ✅ Delete state
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ✅ Stores class_id, section_id, shift_id, day from dayPeriodSubject API response
  const [deletePayloadData, setDeletePayloadData] = useState(null);

  const [classValue, setClassValue] = useState("");
  const [sectionValue, setSectionValue] = useState("");
  const [shiftValue, setShiftValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const [errors, setErrors] = useState({});
  const [subjectStaffData, setSubjectStaffData] = useState({});
  const [staffMap, setStaffMap] = useState({});

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  /* ===========================
     PREFILL DROPDOWNS
  =========================== */
  useEffect(() => {
    if (CLASSID) setClassValue(CLASSID.label);
    if (SECTION) setSectionValue(SECTION.label);
    if (SHIFTID) setShiftValue(SHIFTID.label);
  }, [CLASSID, SECTION, SHIFTID]);

  /* ===========================
     FETCH VIEW DATA
  =========================== */
  useEffect(() => {
    fetchViewData();
  }, [classId, sectionId, shiftId, DAY]);

  const fetchViewData = async () => {
    try {
      setLoading(true);
      const payload = {
        school_id,
        class_id: classId,
        section: sectionId,
        shift_id: shiftId,
        day: DAY,
      };

      const response = await AxiosInstance.post(
        "/timeTable/dayPeriodSubject",
        payload,
      );

      if (response?.data?.status) {
        const apiData = response.data.data || [];

        setSubjectStaffData({ [DAY]: apiData });

        // ✅ Extract class_id, section_id, shift_id, day from first record
        if (apiData.length > 0) {
          const first = apiData[0];
          setDeletePayloadData({
            class_id: first.class_id,
            section_id: first.section_id,
            shift_id: first.shift_id, // ✅ shift_id from API (e.g. 4)
            day: first.day,
          });
        }

        const uniqueSubjectIds = [...new Set(apiData.map((item) => item.id))];
        uniqueSubjectIds.forEach((subjectId) => {
          if (subjectId && !staffMap[subjectId]) {
            fetchStaffList(subjectId);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching timetable:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     FETCH STAFF LIST
  =========================== */
  const fetchStaffList = async (subjectId) => {
    try {
      const response = await AxiosInstance.post("/school/staff/by-subject", {
        subject_id: Number(subjectId),
        school_id,
      });

      if (response?.data?.success) {
        const staffData = response.data.data || [];
        const formattedStaff = staffData.map((staff) => ({
          id: Number(staff.id),
          name: staff.name,
        }));
        setStaffMap((prev) => ({ ...prev, [subjectId]: formattedStaff }));
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  /* ===========================
     TABLE DATA
  =========================== */
  const { formattedData, dynamicPeriods } = useMemo(() => {
    const ALL_DAYS = [
      { id: 1, name: "MONDAY" },
      { id: 2, name: "TUESDAY" },
      { id: 3, name: "WEDNESDAY" },
      { id: 4, name: "THURSDAY" },
      { id: 5, name: "FRIDAY" },
      { id: 6, name: "SATURDAY" },
      { id: 7, name: "SUNDAY" },
    ];

    const TOTAL_PERIODS = 8;
    const periodsArray = Array.from({ length: TOTAL_PERIODS }, (_, i) => i + 1);

    const rows = ALL_DAYS.map((day) => {
      const rowObj = {
        day: day.name,
        day_id: day.id,
        schedule: `Day ${day.id}`,
      };
      periodsArray.forEach((period) => {
        rowObj[`period_${period}`] = "";
      });
      return rowObj;
    });

    return { formattedData: rows, dynamicPeriods: periodsArray };
  }, []);

  /* ===========================
     STAFF CHANGE HANDLER
  =========================== */
  const handleStaffChange = async (rowDayId, period, staffId) => {
    try {
      setLoading(true);
      const payload = {
        id: 0,
        school_id: school_id,
        class_id: classValue.value,
        period: period,
        teaching_id: Number(staffId),
        day: rowDayId,
        start_date: start_date,
        end_date: end_date,
      };

      const response = await AxiosInstance.post(
        "/timeTable/timeTableCheck",
        payload,
      );

      if (response?.data?.status) {
        setSubjectStaffData((prev) => {
          const updated = { ...prev };
          const rowData = [...(updated[rowDayId] || [])];
          const index = rowData.findIndex((p) => p.period === period);
          if (index !== -1) {
            rowData[index] = { ...rowData[index], teaching_id: staffId };
          }
          updated[rowDayId] = rowData;
          return updated;
        });

        setModalState({
          open: true,
          type: "success",
          message: response.data?.message,
          redirectPath: "",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message || "Staff already assigned for this period",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error checking timetable:", error);
      setModalState({
        open: true,
        type: "error",
        message: t.somethingWentWrong,
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     COLUMNS
  =========================== */
  const columns = useMemo(() => {
    return [
      {
        accessorKey: "day",
        header: "Day",
      },
      {
        accessorKey: "schedule",
        header: "Schedule",
        enableSorting: false,
        Cell: ({ row }) => (
          <select
            className="form-select"
            value={DAY ? `Day-${DAY}` : ""}
            disabled
          >
            {DAY && <option value={`Day-${DAY}`}>{`Day-${DAY}`}</option>}
          </select>
        ),
      },
      ...dynamicPeriods.map((period) => ({
        accessorKey: `period_${period}`,
        header: `Period ${period}`,
        Cell: ({ row }) => {
          const rowDayId = row.original.day_id;
          const rowPeriodData = subjectStaffData[rowDayId] || [];
          const periodInfo = rowPeriodData.find(
            (item) => item.period === period,
          );
          const subjectId = periodInfo?.id;
          const staffListForSubject = staffMap[subjectId] || [];

          return (
            <div className={tiemStyles.periodContainer}>
              {periodInfo?.subject_name && (
                <div className={tiemStyles.subjectLabel}>
                  {periodInfo.subject_name}
                </div>
              )}
              <select
                className="form-select form-select-sm"
                value={Number(periodInfo?.teaching_id) || ""}
                onChange={(e) =>
                  handleStaffChange(rowDayId, period, e.target.value)
                }
              >
                <option value="">-Select Staff-</option>
                {staffListForSubject.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.name}
                  </option>
                ))}
              </select>
            </div>
          );
        },
      })),
    ];
  }, [dynamicPeriods, subjectStaffData, staffMap]);

  /* ===========================
     SUBMIT
  =========================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const dayRow = formattedData.find((d) => d.day_id === DAY);
    const dayPeriods = subjectStaffData[DAY] || [];

    const payload = {
      day: DAY,
      day_name: dayRow?.day || "",
      periods: dayPeriods.map((periodItem) => ({
        period: periodItem.period,
        teaching_id: Number(periodItem.teaching_id) || 0,
        course_id: Number(periodItem.course_id) || 0,
        id: Number(periodItem.id) || 0,
      })),
    };

    const response = await AxiosInstance.post(
      "/timeTable/timeTableEdit",
      payload,
    );

    if (response.data?.status) {
      navigate("/user/createtimetbListView");
    } else {
      console.log("error");
    }
  };

  const handleCancel = () => {
    setClassValue("");
    setSectionValue("");
  };

  /* ===========================
     MODAL OK
  =========================== */
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  /* ===========================
     ✅ DELETE HANDLER
     Uses class_id, section_id, shift_id, day
     from dayPeriodSubject API response
  =========================== */
  const handleDelete = async () => {
    if (!deletePayloadData) return;

    setDeleteLoading(true);
    try {
      const payload = {
        school_id: school_id,
        class_id: deletePayloadData.class_id,
        section_id: deletePayloadData.section_id,
        shift_id: deletePayloadData.shift_id, // ✅ from API response
        day: deletePayloadData.day,
      };

      const response = await AxiosInstance.delete(
        "/timeTable/timeTableDelete",
        { data: payload },
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || t.deleteSuccess,
          redirectPath: "",
        });
        setTimeout(() => {
          navigate(-1);
        }, 800);
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || t.deleteFailed,
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t.somethingWentWrong,
        redirectPath: "",
      });
    } finally {
      setDeleteLoading(false);
      setDeleteModal(false);
    }
  };

  /* ===========================
     RENDER
  =========================== */
  return (
    <>
      {/* Success / Error Modal */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? t.success : t.failed}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* ✅ Delete Confirmation Modal */}
      <PopupModal
        open={deleteModal}
        title={t.delete}
        message={t.confirmDeleteTimeTable}
        onOk={handleDelete}
        onClose={() => setDeleteModal(false)}
      />

      <div className={styles.container}>
        <div className="row mb-4">
          {/* SHIFT */}
          <div className="col-lg-4 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.shiftLabel}
              value={shiftValue}
              placeholder={t.shiftPlaceholder}
              required
            />
          </div>
          {/* CLASS */}
          <div className="col-lg-4 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.classLabel}
              value={classValue}
              placeholder={t.classPlaceholder}
              required
              error={errors.classValue}
            />
          </div>
          {/* SECTION */}
          <div className="col-lg-4 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.sectionLabel}
              value={sectionValue}
              placeholder={t.sectionPlaceholder}
              required
              error={errors.sectionValue}
            />
          </div>
        </div>

        {formattedData.length > 0 && (
          <CustomMRTTable
            columns={columns}
            data={formattedData.filter((d) => d.day_id === Number(DAY))}
            loading={loading}
            pagination={pagination}
            onPaginationChange={setPagination}
            rowCount={formattedData.length}
            enablePagination={false}
            enableSearch={false}
            enableFiltering={false}
          />
        )}

        <div className="col-md-12 px-2 mt-2 d-flex justify-content-center align-items-end">
          <div className="d-flex gap-2">
            <CustomButton
              variant="contained"
              onClick={handleSubmit}
              className={styles.saveButton}
              loading={loading}
            >
              {t.saveButton}
            </CustomButton>

            <CustomButton
              variant="contained"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={loading}
            >
              {t.resetButton}
            </CustomButton>

            {/* ✅ Delete button — always visible, no edit mode guard */}
            <CustomButton
              type="button"
              className={styles.deleteButton}
              onClick={() => setDeleteModal(true)}
              disabled={deleteLoading || !deletePayloadData}
            >
              {deleteLoading ? t.deleting : t.delete}
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTimeTable;
